import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Card from "@mui/material/Card";
import { Grid, CircularProgress } from "@mui/material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDEditor from "components/MDEditor";
import MDDatePicker from "components/MDDatePicker";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

import Autocomplete from "@mui/material/Autocomplete";
import Switch from "@mui/material/Switch";

import MapSelectEdit from "./map/index";
import config from "../../../../config";

// --- constants (copied from your existing files, kept identical) ---
const islandTypes = [
  { id: 1, name: "Ρόδος" },
  { id: 2, name: "Κως" },
  { id: 3, name: "Κάλυμνος" },
  { id: 4, name: "Λέρος" },
  { id: 5, name: "Κάρπαθος" },
  { id: 6, name: "Κάσος" },
  { id: 7, name: "Πάτμος" },
  { id: 8, name: "Αστυπάλαια" },
  { id: 9, name: "Νίσυρος" },
  { id: 10, name: "Τήλος" },
  { id: 11, name: "Σύμη" },
  { id: 12, name: "Χάλκη" },
  { id: 13, name: "Λειψοί" },
  { id: 14, name: "Μεγίστη" },
  { id: 15, name: "Αγαθονήσι" },
  { id: 16, name: "Σύρος" },
];

const accidentTypes = [
  { id: 1, name: "Σύγκρουση σε προπορευόμενο όχημα" },
  { id: 2, name: "Μετωπική σύγκρουση" },
  { id: 3, name: "Πλαγιομετωπική σύγκρουση" },
  { id: 4, name: "Πλάγια σύγκρουση" },
  { id: 5, name: "Πρόσκρουση σε σταθμευμένο όχημα" },
  { id: 6, name: "Εκτροπή οχήματος" },
  { id: 7, name: "Ανατροπή οχήματος" },
  { id: 8, name: "Παράσυρση πεζού" },
  { id: 9, name: "Σύγκρουση με ζώο" },
  { id: 10, name: "Άλλο" },
];

const weatherVals = [
  { id: 1, name: "Χωρίς ιδιαίτερες καιρικές συνθήκες" },
  { id: 2, name: "Βροχή" },
  { id: 3, name: "Καταιγίδα" },
  { id: 4, name: "Χαλάζι" },
  { id: 5, name: "Χιονόπτωση" },
  { id: 6, name: "Ομίχλη" },
  { id: 7, name: "Άλλο" },
];

const roadStatusVals = [
  { id: 1, name: "Στεγνό" },
  { id: 2, name: "Υγρό" },
  { id: 3, name: "Λάδια" },
  { id: 4, name: "Χαλίκι" },
  { id: 5, name: "Πάγος" },
  { id: 6, name: "Γλίτσα" },
  { id: 7, name: "Άλλο" },
];

const vehicleTypes = [
  { id: 1, name: "Βαρέα οχήματα & Επαγγελματικά οχήματα > 3,5 τ " },
  { id: 2, name: "Δίκυκλα" },
  { id: 3, name: "Ενοικιαζόμενα" },
  { id: 4, name: "Επαγγελματικά επιβατικά & Επαγγελματικά οχήματα < 3,5 τ " },
  { id: 5, name: "Ποδήλατα" },
  { id: 6, name: "Πεζοί" },
  { id: 7, name: "Ηλεκτρικά πατίνια" },
  { id: 8, name: "Επιβατικό Ι.Χ." },
  { id: 9, name: "Λοιπά οχήματα" },
];

const injuredTypes = [
  { id: 1, name: "Ελαφρά τραυματίες" },
  { id: 2, name: "Βαριά τραυματίες" },
  { id: 3, name: "Νεκροί" },
];

const violationLabels = [
  { id: 1, name: "Διαπιστωμένη υπέρβαση του ορίου ταχύτητας" },
  { id: 2, name: "Αριστερή στροφή σε διπλή διαχωριστική γραμμή" },
  { id: 3, name: "Παραβίαση STOP/Φωτ. σηματοδότη" },
  { id: 4, name: "Παράνομη στάθμευση" },
  { id: 5, name: "Αντικανονικό προσπέρασμα" },
  { id: 6, name: "Επικίνδυνοι ελιγμοί" },
  { id: 7, name: "Παραβίαση προτεραιότητας σε κόμβο" },
  { id: 10, name: "Οδήγηση χωρίς τη νόμιμη άδεια οδήγησης" },
  { id: 9, name: "Οδήγηση υπό την επίδραση αλκοόλ" },
  { id: 8, name: "Παράνομη αναστροφή" },
];

// --- helpers ---
const transformAccidentData = (data) => ({
  ...data,
  involved: Object.entries(data.involved || {}).map(([id, value]) => ({
    id: Number(id),
    value,
  })),
  injured: Object.entries(data.injured || {}).map(([id, value]) => ({
    id: Number(id),
    value,
  })),
  violations: Object.entries(data.violations || {})
    .filter(([_, isTrue]) => isTrue)
    .map(([id]) => Number(id)),
});

const AccidentEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const API_TOKEN = config.MAP_MATCHING_API_TOKEN;
  const API_URL = config.MAP_MATCHING_API_URL;

  const [loading, setLoading] = useState(true);

  // form state
  const [type, setType] = useState("");
  const [weather, setWeather] = useState("");
  const [roadStatus, setRoadStatus] = useState("");
  const [description, setDescription] = useState("");
  const [tseoi, setTseoi] = useState("");
  const [timerange, setTimeRange] = useState("");
  const [position, setPosition] = useState(null); // [lon, lat]
  const [island, setIsland] = useState("");

  const [selectedCounts, setSelectedCounts] = useState({});
  const [selectedInjured, setSelectedInjured] = useState({});
  const [selectedViolations, setSelectedViolations] = useState({});

  // notifications
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);

  const successNotification = (
    <MDSnackbar
      color="success"
      icon="check"
      title="Ατυχήματα Ειδοποίηση"
      content="Επιτυχής ενημέρωση ατυχήματος"
      dateTime={new Date().toLocaleTimeString()}
      open={successSB}
      onClose={() => setSuccessSB(false)}
      close={() => setSuccessSB(false)}
      bgWhite
    />
  );

  const errorNotification = (
    <MDSnackbar
      color="error"
      icon="warning"
      title="Ατυχήματα Ειδοποίηση"
      content="Προέκυψε σφάλμα"
      dateTime={new Date().toLocaleTimeString()}
      open={errorSB}
      onClose={() => setErrorSB(false)}
      close={() => setErrorSB(false)}
      bgWhite
    />
  );

  // fetch by id (same as your view page)
  useEffect(() => {
    const fetchAccident = async () => {
      try {
        setLoading(true);

        const url = `${API_URL}/accidents/${id}`;
        const response = await axios.get(url, {
          headers: {
            "X-API-Key": API_TOKEN,
            accept: "*/*",
          },
        });

        const acc = response.data;

        setIsland(acc.island ?? "");
        setType(acc.type_accident ?? "");
        setWeather(acc.weather ?? "");
        setRoadStatus(acc.road ?? "");
        setDescription(acc.description ?? "");
        setTimeRange(acc.date ?? "");
        setTseoi(acc.police_report ?? "");

        if (acc.position && acc.position.length === 2) {
          setPosition(acc.position); // [lon, lat]
        }

        const counts = {};
        (acc.involved || []).forEach((item) => {
          counts[item.id] = item.value;
        });
        setSelectedCounts(counts);

        const injuredMap = {};
        (acc.injured || []).forEach((item) => {
          injuredMap[item.id] = item.value;
        });
        setSelectedInjured(injuredMap);

        const violationsMap = {};
        (acc.violations || []).forEach((vId) => {
          violationsMap[vId] = true;
        });
        setSelectedViolations(violationsMap);
      } catch (e) {
        console.error(e);
        setErrorSB(true);
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchAccident();
  }, [id, API_URL, API_TOKEN]);

  const handleDateChange = (date) => {
    if (date && date[0]) setTimeRange(date[0].toISOString());
  };

  const handleSave = async () => {
    if (!id) return;

    const payload = transformAccidentData({
      id: Number(id),
      island,
      type_accident: type,
      weather,
      road: roadStatus,
      description,
      date: timerange,
      // IMPORTANT: keep backend as [lon, lat]. (your view page reverses; but that mutates state!)
      position: Array.isArray(position) ? [position[1], position[0]] : null,
      involved: selectedCounts,
      violations: selectedViolations,
      injured: selectedInjured,
      severity: 1,
      police_report: tseoi,
    });

    try {
      const url = `${API_URL}/accidents/${id}`;
      await axios.put(url, payload, {
        headers: {
          "X-API-Key": API_TOKEN,
          "Content-Type": "application/json",
        },
      });

      setSuccessSB(true);
        setTimeout(() => {
        navigate("/list/accidents"); // adjust if your route differs
        }, 1000);
    } catch (e) {
      console.error(e);
      setErrorSB(true);
    }
  };

  const canSave = useMemo(() => {
    return Boolean(island) && Boolean(type) && Boolean(timerange) && Array.isArray(position);
  }, [island, type, timerange, position]);

  if (loading) {
    return (
      <DashboardLayout>
        <MDBox mt={4} display="flex" justifyContent="center" alignItems="center">
          <CircularProgress />
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <MDBox mt={4} p={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <Card sx={{ overflow: "visible" }}>
              <MDBox p={3}>
                <MDTypography variant="h5">Επεξεργασία Ατυχήματος #{id}</MDTypography>
              </MDBox>

              <MDBox component="form" pb={3} px={3}>
                <Grid container spacing={3}>
                  {/* Island */}
                  <Grid item xs={12}>
                    <MDTypography component="label" variant="button" fontWeight="regular" color="text">
                      Νησί στο οποίο έγινε το ατύχημα *
                    </MDTypography>
                    <Autocomplete
                      value={islandTypes.find((v) => v.id === island) || null}
                      options={islandTypes}
                      getOptionLabel={(o) => o.name}
                      onChange={(e, v) => setIsland(v?.id ?? "")}
                      renderInput={(params) => <MDInput {...params} variant="standard" />}
                    />
                  </Grid>

                  {/* Date */}
                  <Grid item xs={12} sm={6} container direction="column">
                    <MDTypography component="label" variant="button" fontWeight="regular" color="text">
                      Ημερομηνία *
                    </MDTypography>
                    <MDDatePicker
                      input={{ placeholder: "dd/mm/yyyy HH:mm" }}
                      value={timerange ? new Date(timerange) : null}
                      options={{
                        enableTime: true,
                        dateFormat: "d/m/Y H:i",
                        altFormat: "d/m/Y H:i",
                        time_24hr: true,
                      }}
                      onChange={handleDateChange}
                    />
                  </Grid>

                  {/* Position */}
                  <Grid item xs={12} container direction="column">
                    <MDTypography component="label" variant="button" fontWeight="regular" color="text">
                      Θέση Ατυχήματος *
                    </MDTypography>
                    {!position ? (
                      <MDTypography component="label" variant="button" fontWeight="regular" color="text">
                        Επέλεξε Θέση στο Χάρτη
                      </MDTypography>
                    ) : (
                      <>
                        <MDTypography component="label" variant="button" fontWeight="regular" color="text">
                          Lat: {position[1]}
                        </MDTypography>
                        <MDTypography component="label" variant="button" fontWeight="regular" color="text">
                          Long: {position[0]}
                        </MDTypography>
                      </>
                    )}
                  </Grid>

                  {/* Type */}
                  <Grid item xs={12}>
                    <MDTypography component="label" variant="button" fontWeight="regular" color="text">
                      Κατηγορία Ατυχήματος *
                    </MDTypography>
                    <Autocomplete
                      value={accidentTypes.find((v) => v.id === type) || null}
                      options={accidentTypes}
                      getOptionLabel={(o) => o.name}
                      onChange={(e, v) => setType(v?.id ?? "")}
                      renderInput={(params) => <MDInput {...params} variant="standard" />}
                    />
                  </Grid>

                  {/* Weather */}
                  <Grid item xs={12}>
                    <MDTypography component="label" variant="button" fontWeight="regular" color="text">
                      Κατάσταση Καιρού *
                    </MDTypography>
                    <Autocomplete
                      value={weatherVals.find((v) => v.id === weather) || null}
                      options={weatherVals}
                      getOptionLabel={(o) => o.name}
                      onChange={(e, v) => setWeather(v?.id ?? "")}
                      renderInput={(params) => <MDInput {...params} variant="standard" />}
                    />
                  </Grid>

                  {/* Road */}
                  <Grid item xs={12}>
                    <MDTypography component="label" variant="button" fontWeight="regular" color="text">
                      Κατάσταση Δρόμου *
                    </MDTypography>
                    <Autocomplete
                      value={roadStatusVals.find((v) => v.id === roadStatus) || null}
                      options={roadStatusVals}
                      getOptionLabel={(o) => o.name}
                      onChange={(e, v) => setRoadStatus(v?.id ?? "")}
                      renderInput={(params) => <MDInput {...params} variant="standard" />}
                    />
                  </Grid>

                  {/* Involved */}
                  <Grid item xs={12}>
                    <MDBox mb={1}>
                      <MDTypography component="label" variant="button" fontWeight="regular" color="text">
                        Καταχωρήστε το πλήθος των εμπλεκόμενων *
                      </MDTypography>
                    </MDBox>
                    <MDBox display="grid" gridTemplateColumns={{ xs: "1fr" }} gap={2}>
                      {vehicleTypes.map((vehicle) => (
                        <MDBox key={vehicle.id} sx={{ minWidth: 170 }}>
                          <MDInput
                            type="number"
                            label={vehicle.name}
                            variant="standard"
                            value={selectedCounts[vehicle.id] ?? 0}
                            onChange={(e) =>
                              setSelectedCounts((prev) => ({
                                ...prev,
                                [vehicle.id]: e.target.value === "" ? "" : Math.max(0, parseInt(e.target.value) || 0),
                              }))
                            }
                            fullWidth
                            inputProps={{ min: 0 }}
                          />
                        </MDBox>
                      ))}
                    </MDBox>
                  </Grid>

                  {/* Violations */}
                  <Grid item xs={12}>
                    <MDBox mb={1}>
                      <MDTypography component="label" variant="button" fontWeight="regular" color="text">
                        Αίτια Ατυχήματος *
                      </MDTypography>
                    </MDBox>
                    <MDBox
                      display="grid"
                      gridTemplateColumns={{ xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }}
                      gap={2}
                    >
                      {violationLabels.map((v) => (
                        <MDBox
                          key={v.id}
                          sx={{
                            minWidth: 170,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            boxShadow: 2,
                            borderRadius: 2,
                          }}
                        >
                          <MDTypography variant="button" fontWeight="regular" sx={{ mb: 1, textAlign: "center" }}>
                            {v.name}
                          </MDTypography>
                          <Switch
                            checked={!!selectedViolations[v.id]}
                            onChange={(e) =>
                              setSelectedViolations((prev) => ({
                                ...prev,
                                [v.id]: e.target.checked,
                              }))
                            }
                          />
                        </MDBox>
                      ))}
                    </MDBox>
                  </Grid>

                  {/* Injured */}
                  <Grid item xs={12}>
                    <MDBox mb={1}>
                      <MDTypography component="label" variant="button" fontWeight="regular" color="text">
                        Καταχωρήστε το πλήθος των παθόντων *
                      </MDTypography>
                    </MDBox>
                    <MDBox display="grid" gridTemplateColumns={{ xs: "1fr", lg: "repeat(3, 1fr)" }} gap={2}>
                      {injuredTypes.map((it) => (
                        <MDBox key={it.id} sx={{ minWidth: 170 }}>
                          <MDInput
                            type="number"
                            label={it.name}
                            variant="standard"
                            value={selectedInjured[it.id] ?? 0}
                            onChange={(e) =>
                              setSelectedInjured((prev) => ({
                                ...prev,
                                [it.id]: e.target.value === "" ? "" : Math.max(0, parseInt(e.target.value) || 0),
                              }))
                            }
                            fullWidth
                            inputProps={{ min: 0 }}
                          />
                        </MDBox>
                      ))}
                    </MDBox>
                  </Grid>

                  {/* TSEOI */}
                  <Grid item xs={12}>
                    <MDBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                      <MDTypography component="label" variant="button" fontWeight="regular" color="text">
                        Αναγνωριστικός Αριθμός Τροχαίας
                      </MDTypography>
                    </MDBox>
                    <MDInput fullWidth type="text" value={tseoi || ""} onChange={(e) => setTseoi(e.target.value)} />
                  </Grid>

                  {/* Description */}
                  <Grid item xs={12}>
                    <MDBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                      <MDTypography component="label" variant="button" fontWeight="regular" color="text">
                        Σύντομη Περιγραφή/Παρατηρήσεις
                      </MDTypography>
                    </MDBox>
                    <MDEditor value={description || ""} onChange={(v) => requestAnimationFrame(() => setDescription(v))} />
                  </Grid>

                  {/* Buttons */}
                  <Grid mt={3} item xs={12}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center">
                      <MDButton
                        variant="outlined"
                        color="secondary"
                        onClick={() => navigate("/list/accidents")}
                        sx={{ "&.MuiButton-outlined": { textTransform: "capitalize !important" } }}
                      >
                        Ακύρωση
                      </MDButton>

                      <MDButton
                        variant="gradient"
                        color="info"
                        onClick={handleSave}
                        disabled={!canSave}
                        sx={{ "&.MuiButton-contained": { textTransform: "capitalize !important" } }}
                      >
                        Αποθήκευση
                      </MDButton>
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>

          {/* Map */}
          <Grid item xs={12} md={7}>
            <div className="mapContainer100">
              <MapSelectEdit selectedPoint={position} setSelectedPoint={setPosition} />
            </div>
          </Grid>

          {successSB && successNotification}
          {errorSB && errorNotification}
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
};

export default AccidentEdit;
